import React from "react";
const WidgetValues = () => {
  return (
    <div id={"widget-values"}>
      <h3>Widget Values</h3>


      <p>
        The Widget Values toolbox category contains all blocks for interacting
        with SOTI Snap widgets. Open the Widget Values toolbox category to
        reveal a list of blocks for all the widgets that appear in your Snap
        app. These blocks are categorized by widget type.
      </p>

      <div className={"imageContainer"}>
        <img src="/images/widgetValuesIcon.png" alt="Widgets" />
      </div>

      <p>
        Each Widget Value block has a dropdown list where you can select which
        widget in your SOTI Snap app the block applies to. The dropdown list contains the names of the widgets that the
        block can be used for.
      </p>

      <h3 id={"barcode-scanner"}>Barcode Scanner</h3>
      <div className={"imageContainer"}>
        <img src="/images/barcode_scanner.png" alt="Barcode Scanner" />
      </div>

      <p>
        The "get list of values scanned by barcode" block allows you to retrieve a list of values scanned by the selected barcode scanner.
      </p>

      <p>
        It retrieves the barcodes you’ve scanned and returns them as a list of codes
        (like product numbers or IDs). You can use these codes to look up items, auto-fill forms,
        or perform actions such as adding products to a cart or viewing item details.
      </p>

      <h3 id={"buttons"}>Buttons</h3>
      <div className={"imageContainer"}>
        <img src="/images/button.png" alt="Buttons" />
      </div>

      <p>
        The Set Button Text block allows you to change the text shown on a button after it has been added to your app.
      </p>

      <h3 id={"charts"}>Charts</h3>
      <div className={"imageContainer"}>
        <img src="/images/charts.png" alt="Charts" />
      </div>

      <p>
        The Refresh Chart block allow you to refresh a chart that you have placed in your app.
      </p>

      <h3 id={"checkbox-lists"}>Checkbox Lists</h3>

      <div className={"imageContainer"}>
        <img src="/images/checkbox_list.png" alt="Checkbox Lists" />
      </div>

      <p>
        The Checkbox List blocks give you different ways to work with checkboxes in your app:
      </p>

      <ul>
        <li>
          <b>Set checkbox with text</b> - Use this block to select a specific checkbox (by its text label) and set it
          to either checked or unchecked. This is useful if you want to pre-fill a form or automatically select an option.
        </li>
        <br />
        <li>
          <b>Checkbox is checked</b> - This block lets you test whether a specific checkbox
          (by its text label) is currently checked. It is helpful when you want to run actions only if a certain option has been chosen.
        </li>
        <br />
        <li>
          <b>Get list of values of checked options in</b> - With this block, you can retrieve a list of the values that the user has selected in the checkbox list
          (e.g., ["Option 1", "Option 3"]).
        </li>
      </ul>

      <h3 id={"dates"} >Dates</h3>
      <div className={"imageContainer"}>
        <img src="/images/dates.png" alt="Dates" />
      </div>

      <p>
        Date Picker blocks allow you to display and work with dates in your app.
        You can use different blocks to get or set dates, clear them, or customize the placeholder text.
      </p>

      <ul>
        <li>
          <b>Get date</b> - Retrieves the currently selected date from the date picker.
        </li>
        <br />

        <li>
          <b>Set date</b> - Allows you to programmatically set the date picker to a specific date (for example, <code>2025-08-20</code>).
          This is helpful for pre-filling a form with a default date.
        </li>
        <br />
        <li>
          <b>Reset date</b> - Clears the date picker so that no date is selected.
        </li>
        <br />
        <li>
          <b>Set placeholder text</b> - Updates the placeholder text that appears when no date has been chosen.
          For example, you might set it to “Select a date” to inform the user to select a date.
        </li>
      </ul>

      <h3 id={"dropdowns"}>Dropdowns</h3>
      <div className={"imageContainer"}>
        <img src="/images/dropdowns.png" alt="Dropdowns" />
      </div>

      <p>
        The Dropdown blocks allow you to manage and interact with dropdown menus in your app.
        You can use blocks to add options, retrieve selected values, and dynamically update dropdown content.
      </p>

      <ul>
        <li>
          <b>Add option</b> - Inserts a new option into a dropdown.
        </li>
        <br />
        <li>
          <b>Remove option</b> - Remove specific options from a dropdown.
        </li>
        <br />
        <li>
          <b>Get selected value</b> - Retrieves the currently selected value from a dropdown.
        </li>
        <br />
        <li>
          <b>Set dropdown by text</b> - Updates the selected value of a dropdown. You can update the selected value by display text or by unique ID (as configured in the Snap IDE).
        </li>
        <br />
        <li>
          <b>Get text/unique ID of selected option in dropdown</b> - Checks if the selected value of the dropdown is equal to the specified text or unique ID.
        </li>
      </ul>

      <h3 id={"external-data"}>External Data</h3>
      <div className={"imageContainer"}>
        <img src="/images/external_data.png" alt="External Data" />
      </div>

      <p>
        Using the External Data block, you can access a specific column from the data stored in your External Data widget in Blockly.
      </p>

      <p>
        Before using this block, make sure you have:
      </p>

      <ul>
        <li>Uploaded a data source (such as an Excel file)</li>
        <br />
        <li>Added an External Data widget to your Snap app</li>
      </ul>

      <p>
        For example, if your Excel file contains a column labelled <em>Last name</em>, you can use this block to retrieve all the values from that column,
        either as a list of values or a single line of text.
      </p>

      <h3 id={"file-uploads"}>File Uploads</h3>
      <div className={"imageContainer"}>
        <img src="/images/file_uploads.png" alt="File Uploads" />
      </div>

      <p>
        The <strong>File Upload</strong> blocks allow users to upload files into your app.
      </p>

      <h4>From the selected File Upload widget, you can retrieve the following:</h4>
      <ul>
        <li><strong>File name</strong> – the name of the uploaded file</li>
        <li><strong>File size</strong> – the size of the file in bytes</li>
        <li><strong>File extension</strong> – the file extension, such as <code>.pdf</code>, <code>.jpg</code>, or <code>.xlsx</code></li>
        <li><strong>Base64 content</strong> – the file's data encoded as a base64 string</li>
      </ul>


      <h3 id={"group-list"}>Group List</h3>
      <div className={"imageContainer"}>
        <img src="/images/group_list.png" alt="Group List" />
      </div>

      <p>
        The <strong>Group List</strong> block allows you to get user groups that are defined in your organization's Snap settings.
        These groups can be found under <em>Settings &gt; Users and Permissions &gt; Groups</em>.
      </p>

      <h3 id={"hamburger-menu"}>Hamburger Menu</h3>
      <div className={"imageContainer"}>
        <img src="/images/hamburgers.png" alt="Hamburger Menu Widget" />
      </div>

      <p>
        The Hamburger Menu blocks allow you to control and customize the navigation menu that appears in your app.
        You can use these blocks to open the menu, enable or disable it on specific pages, retrieve or set its name, and manage its icon image.
      </p>

      <ul>
        <li>
          <b>Open hamburger menu</b> - Opens the selected hamburger menu. Choose <em>Active hamburger menu</em> to open whichever menu is currently active.
        </li>
        <br />

        <li>
          <b>Set Enable/Disable hamburger menu for a page</b> - Use this block to control whether the hamburger menu is active on a specific page.
        </li>
        <br />

        <li>
          <b>Get hamburger menu name</b> - Retrieves the current name of the hamburger menu.
        </li>
        <br />
        <li>
          <b>Set hamburger menu name to " "</b> - Updates the name of the hamburger menu.
        </li>

        <br />

        <li>
          <b>Get hamburger menu image</b> - Retrieves the current image URL used for the hamburger menu.
        </li>
        <br />
        <li>
          <b>Set hamburger menu image to " "</b> - Assigns a new image URL to the hamburger menu.
        </li>
      </ul>


      <h3 id={"images"}>Images</h3>
      <div className={"imageContainer"}>
        <img src="/images/images.png" alt="Images section" />
      </div>


      <p>
        The Image blocks allow you to display and update images dynamically within your app.
        You can use these blocks to retrieve the current image source or assign a new one.
      </p>

      <ul>
        <li>
          <b>Get image source</b> - Retrieves the source URL of the image or a base64 string encoding of the image.
        </li>
        <br />

        <li>
          <b>Set image source</b> - Updates the image shown in the widget by assigning a new source.
        </li>
      </ul>

      <h3 id={"labels"}>Labels</h3>
      <div className={"imageContainer"}>
        <img src="/images/labels.png" alt="Labels" />
      </div>


      <p>
        The Label blocks allow you to display and update text dynamically within your app.
        You can use these blocks to set or retrieve the text shown in a label.
      </p>

      <ul>
        <li>
          <b>Set label text</b> - Updates the text displayed in a label widget.
        </li>

        <br />

        <li>
          <b>Get label text</b> - Retrieves the current text from a label widget.
        </li>
      </ul>


      <h3 id={"layouts"} >Layouts</h3>
      <div className={"imageContainer"}>
        <img src="/images/layouts.png" alt="Layouts" />
      </div>

      <p>
        The Layout blocks allow you to customize the appearance of layout widgets in your app.
        Using the <b>set layout colour to</b> block, you can set the background colour of a layout to match your design preferences or app theme.
      </p>


      <h3 id={"maps"}>Maps</h3>
      <div className={"imageContainer"}>
        <img src="/images/maps.png" alt="Maps" />
      </div>

      <p>
        You can use the Maps blocks to add or remove markers and control the zoom level to tailor the map view for your users.
      </p>

      <ul>
        <li>
          <b>Add marker</b> - Places a marker on the map at a specific latitude and longitude and returns its Marker ID. You can provide a unique Marker ID or leave it empty to generate one automatically. Marker IDs must be unique within the same map. Store the returned ID in a variable if you need to remove the marker later.

        </li>
        <br />
        <li>
          <b>Remove marker</b> - Removes the marker from the map with the specified Marker ID.

        </li>
        <br />
        <li>
          <b>Set zoom level</b> - Adjusts the zoom of the map using an integer value. This controls how much of the map is visible, from a global view to street-level detail.
          <br />

          <br />
          Refer to the zoom level guide:
          <ul>
            <li><b>1–5:</b> World or continent view</li>
            <li><b>6–10:</b> Country or city view</li>
            <li><b>11–15:</b> Streets and neighbourhoods</li>
            <li><b>16–20:</b> Buildings and detailed areas</li>
          </ul>
        </li>
      </ul>

      <h3 id={"radio-buttons"}>Radio Buttons</h3>
      <div className={"imageContainer"}>
        <img src="/images/radio_buttons.png" alt="Radio Button" />
      </div>

      <p>
        You can use these blocks to set, retrieve, or check the selected value of a radio button.</p>

      <ul>
        <li>
          <b>Set selected option</b> - Selects the specified radio button option by its text or value. The selected option must already exist in the list of radio buttons.
        </li>
        <br />

        <li>
          <b>Get selected option</b> - Retrieves the currently selected value, text, or both from the radio button widget.
        </li>
        <br />

        <li>
          <b>Check selected value</b> - Checks whether the radio button is currently set to a specific value and returns true or false.
        </li>
      </ul>

      <h3 id={"rating"} >Rating</h3>
      <div className={"imageContainer"}>
        <img src="/images/ratings.png" alt="Ratings" />
      </div>

      <p>
        You can use these blocks to retrieve or set the number of stars selected in a Rating widget.
      </p>

      <ul>
        <li>
          <b>Get current rating</b> - Retrieves the number of stars currently selected by the user.
        </li>
        <br />
        <li>
          <b>Set rating</b> - Assigns a specific number of stars to the selected Rating widget.
        </li>
      </ul>

      <h3 id={"sections"}>Sections</h3>
      <div className={"imageContainer"}>
        <img src="/images/sections.png" alt="Sections" />
      </div>

      <p>
        Use this block to assign a background colour to the selected Section widget.
      </p>

      <h3 id={"switches"}>Switches</h3>
      <div className={"imageContainer"}>
        <img src="/images/switches.png" alt="Switches" />
      </div>

      <p>
        You can use these blocks to read or update the state of a Switch widget.
      </p>

      <ul>
        <li>
          <b>Get switch state</b> - Retrieves the current state of the switch. The result is either <code>true</code> (on) or <code>false</code> (off).
        </li>
        <br />
        <li>
          <b>Set switch state</b> - Updates the switch to either <code>true</code> (on) or <code>false</code> (off).
        </li>
      </ul>

      <h3 id={"textboxes"}>Textboxes</h3>

      <div className={"imageContainer"}>
        <img src="/images/textboxes.png" alt="Textboxes" />
      </div>

      <p>
        You can use these blocks to retrieve, update, and style the content of a Textbox widget.
      </p>

      <ul>
        <li>
          <b>Get textbox value</b> - Retrieves the current text entered in the selected textbox.
        </li>
        <br />
        <li>
          <b>Set textbox value</b> - Updates the text shown in the textbox. You can use this to pre-fill fields or reset them.
        </li>
        <br />
        <li>
          <b>Set background color</b> - Changes the background colour of the textbox.
        </li>
        <br />
        <li>
          <b>Set placeholder text</b> - Updates the placeholder text that appears when the textbox is empty.
        </li>
      </ul>



      <h3 id={"timers"}>Timers</h3>

      <div className={"imageContainer"}>
        <img src="/images/timers.png" alt="Timers" />
      </div>


      <p>
        The Timer blocks allow you to control and display elapsed time in your app. You can start, stop, reset the timer, and retrieve its value in different formats.
      </p>

      <ul>
        <li>
          <b>Start timer</b> - Begins counting time from the current value. This is useful for tracking how long a user spends on a task or activity.
        </li>
        <br />
        <li>
          <b>Stop timer</b> - Pauses the timer at its current value. You can resume it later or use the value for calculations or display.
        </li>
        <br />
        <li>
          <b>Reset timer</b> - Resets the timer back to zero. This is helpful when restarting a task or clearing previous timing data.
        </li>
        <br />
        <li>
          <b>Get timer value</b> - Retrieves the current timer value. You can choose to output it either as a formatted string (e.g., <code>"0:00:05"</code> for hours, minutes, and seconds) or as a raw number representing the total seconds elapsed.
        </li>
      </ul>

      <h3 id={"user"}>User</h3>

      <div className={"imageContainer"}>
        <img src="/images/User.png" alt="User" />
      </div>

      <p>
        These blocks allow you to retrieve information about the current app user. You can use them to personalize the app experience and validate access.
      </p>

      <ul>
        <li>
          <strong>Get user is logged in</strong> - Returns <code>true</code> or <code>false</code> depending on whether the user is currently logged in.
          You can place additional blocks inside this one to run actions conditionally if the user is logged in.
        </li>
        <br />
        <li>
          <strong>Get user field ID</strong> - Retrieves the value of a specific field from the user's profile using the field ID.
          You can select from a variety of fields such as:
          <ul>
            <li><code>id</code></li>
            <li><code>firstName</code></li>
            <li><code>lastName</code></li>
            <li><code>fullName</code></li>
            <li><code>email</code></li>
          </ul>
        </li>
      </ul>

      <h3 id={"user-lists"}>User Lists</h3>

      <div className={"imageContainer"}>
        <img src="/images/user_lists.png" alt="User Lists" />
      </div>
      <p>
        The <b> get user list </b> block is used to retrieve the list of users selected from a User List widget in your app.
        This block returns an array of selected users.
      </p>

    </div>
  );
};

export default WidgetValues;
